"use client";

import { useMemo } from "react";
import { useTable } from "spacetimedb/react";
import { toast } from "sonner";
import { tables } from "@/module_bindings";
import { useRowTransitions } from "@/hooks/spacetime/use-row-transitions";
import type { Organization } from "@/types/organization";
import type { MyOrganizationRow } from "@/lib/stdb-map";

/**
 * Spec events #6/#7: current-org boolean flags flipping ON
 * (is_in_grace_period, is_glaci_enabled). One useRowTransitions instance
 * per flag — the hook tracks a single state string per row. Optional flags
 * normalize via String(Boolean(...)), so undefined == false and an
 * undefined -> false write is not a transition.
 */
export function OrgFlagToasts({
  organization,
}: {
  organization: Organization | null;
}) {
  const [rows, isReady] = useTable(tables.my_organizations);
  const currentOrgRows = useMemo(
    () =>
      organization
        ? rows.filter((row: MyOrganizationRow) => row.id === organization.id)
        : [],
    [rows, organization],
  );

  useRowTransitions<MyOrganizationRow>({
    rows: currentOrgRows,
    isReady,
    getId: (row) => row.id,
    getState: (row) => String(Boolean(row.isInGracePeriod)),
    resetKey: organization?.id ?? null,
    onTransition: ({ next, row }) => {
      if (next !== "true") return;
      toast.info("Grace Period Started", {
        id: `org-grace-${row.id}-true`,
        description: "Your organization has entered a grace period.",
      });
    },
  });

  useRowTransitions<MyOrganizationRow>({
    rows: currentOrgRows,
    isReady,
    getId: (row) => row.id,
    getState: (row) => String(Boolean(row.isGlaciEnabled)),
    resetKey: organization?.id ?? null,
    onTransition: ({ next, row }) => {
      if (next !== "true") return;
      toast.success("Glaci Access Granted", {
        id: `org-glaci-${row.id}-true`,
        description: "Glaci access has been granted to your organization.",
      });
    },
  });

  return null;
}
